/**
 * Cross-field reconciliation for Parsed1040Return.
 *
 * The 1040 has several fields that are sums of others. If the parser
 * extracts both the components and the total, the total should equal
 * the sum (within $1 for rounding). Mismatches indicate either a
 * parser bug or a non-standard PDF layout, and the advisor should
 * verify before building a tax strategy on the imported numbers.
 *
 * Validation never fails the import — it just appends warnings.
 */
import type {
  Parsed1040Return,
  ExtractionWarning,
  ExtractedField,
} from './parse1040Types';

/** Sum a list of ExtractedFields, treating undefined as 0. */
function sumFields(...fields: Array<ExtractedField<number> | undefined>): number {
  return fields.reduce((acc, f) => acc + (f?.value ?? 0), 0);
}

/** Reconciliation tolerance — IRS forms round to whole dollars but
 *  some tax software keeps decimals internally. $1 absorbs rounding. */
const RECONCILE_TOLERANCE = 1;

export function validate1040Return(parsed: Parsed1040Return): ExtractionWarning[] {
  const warnings: ExtractionWarning[] = [];

  // ── Line 9: Total income = 1z + 2b + 3b + 4b + 5b + 6b + 7 + 8 ───
  // Line 1z is the total of 1a–1h; fall back to 1a when 1z wasn't read.
  if (parsed.line9_totalIncome) {
    const expected = sumFields(
      parsed.line1z_totalWages ?? parsed.line1a_wages,
      parsed.line2b_taxableInterest,
      parsed.line3b_ordinaryDividends,
      parsed.line4b_iraTaxable,
      parsed.line5b_pensionsTaxable,
      parsed.line6b_socialSecurityTaxable,
      parsed.line7_capitalGainOrLoss,
      parsed.line8_otherIncomeSch1,
    );
    const diff = Math.abs(parsed.line9_totalIncome.value - expected);
    if (diff > RECONCILE_TOLERANCE) {
      warnings.push({
        field: 'line9_totalIncome',
        severity: 'warning',
        message: `Line 9 (Total Income, ${money(parsed.line9_totalIncome.value)}) doesn't match the sum of its components (${money(expected)}, off by ${money(diff)}). Verify the imported income line items before saving.`,
      });
    }
  }

  // ── Line 11: AGI = line 9 - line 10 ───────────────────────────────
  if (parsed.line11_agi && parsed.line9_totalIncome) {
    const adjustments = parsed.line10_adjustments?.value ?? 0;
    const expected = parsed.line9_totalIncome.value - adjustments;
    const diff = Math.abs(parsed.line11_agi.value - expected);
    if (diff > RECONCILE_TOLERANCE) {
      warnings.push({
        field: 'line11_agi',
        severity: 'warning',
        message: `Line 11 (AGI, ${money(parsed.line11_agi.value)}) doesn't match line 9 minus line 10 (${money(expected)}, off by ${money(diff)}). Verify before saving.`,
      });
    }
  }

  // ── 3a should not exceed 3b: qualified dividends ≤ ordinary dividends ─
  if (parsed.line3a_qualifiedDividends && parsed.line3b_ordinaryDividends) {
    if (parsed.line3a_qualifiedDividends.value > parsed.line3b_ordinaryDividends.value + RECONCILE_TOLERANCE) {
      warnings.push({
        field: 'line3a_qualifiedDividends',
        severity: 'warning',
        message: `Qualified dividends (${money(parsed.line3a_qualifiedDividends.value)}) exceed ordinary dividends (${money(parsed.line3b_ordinaryDividends.value)}). The wizard will reject this — fix on the Income step before saving.`,
      });
    }
  }

  // ── 4b ≤ 4a: taxable IRA ≤ gross IRA distributions ────────────────
  if (parsed.line4a_iraDistributions && parsed.line4b_iraTaxable) {
    if (parsed.line4b_iraTaxable.value > parsed.line4a_iraDistributions.value + RECONCILE_TOLERANCE) {
      warnings.push({
        field: 'line4b_iraTaxable',
        severity: 'warning',
        message: `Taxable IRA distributions (${money(parsed.line4b_iraTaxable.value)}) exceed gross IRA distributions (${money(parsed.line4a_iraDistributions.value)}). Verify.`,
      });
    }
  }

  // ── 5b ≤ 5a: taxable pensions ≤ gross pensions ───────────────────
  if (parsed.line5a_pensions && parsed.line5b_pensionsTaxable) {
    if (parsed.line5b_pensionsTaxable.value > parsed.line5a_pensions.value + RECONCILE_TOLERANCE) {
      warnings.push({
        field: 'line5b_pensionsTaxable',
        severity: 'warning',
        message: `Taxable pensions (${money(parsed.line5b_pensionsTaxable.value)}) exceed gross pensions (${money(parsed.line5a_pensions.value)}). Verify.`,
      });
    }
  }

  // ── 6b ≤ 6a: taxable SS ≤ gross SS (federal max is 85% of gross) ─
  if (parsed.line6a_socialSecurityGross && parsed.line6b_socialSecurityTaxable) {
    const max85 = parsed.line6a_socialSecurityGross.value * 0.85;
    if (parsed.line6b_socialSecurityTaxable.value > max85 + RECONCILE_TOLERANCE) {
      warnings.push({
        field: 'line6b_socialSecurityTaxable',
        severity: 'warning',
        message: `Taxable Social Security (${money(parsed.line6b_socialSecurityTaxable.value)}) exceeds 85% of gross benefits (${money(max85)}). Verify — this is the federal cap.`,
      });
    }
  }

  // ── Schedule C: expenses without receipts ────────────────────────
  if (parsed.schedCTotalExpenses && !parsed.schedCGrossReceipts) {
    warnings.push({
      field: 'schedCGrossReceipts',
      severity: 'info',
      message: 'Schedule C total expenses imported but gross receipts not detected. Confirm the business income on the Business step.',
    });
  }

  // ── Schedule D + 1040 line 7: short+long should equal line 7 ─────
  if (parsed.line7_capitalGainOrLoss && parsed.shortTermCapitalGains && parsed.longTermCapitalGains) {
    const expected = parsed.shortTermCapitalGains.value + parsed.longTermCapitalGains.value;
    const diff = Math.abs(parsed.line7_capitalGainOrLoss.value - expected);
    if (diff > RECONCILE_TOLERANCE) {
      warnings.push({
        field: 'line7_capitalGainOrLoss',
        severity: 'warning',
        message: `1040 line 7 (${money(parsed.line7_capitalGainOrLoss.value)}) doesn't equal Schedule D short-term + long-term (${money(expected)}). Verify.`,
      });
    }
  }

  // ── QBI inputs: SSTB without W-2 wages is suspicious ─────────────
  if (parsed.qbiSSTB?.value && !parsed.qbiW2Wages) {
    warnings.push({
      field: 'qbiW2Wages',
      severity: 'info',
      message: 'SSTB classification detected but no QBI W-2 wages. For SSTB above the income threshold, the deduction phases out — verify W-2 wages on Form 8995-A.',
    });
  }

  return warnings;
}

function money(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}
